import {
  Button,
  Flex,
  FormControl,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { Branch } from '@texas/api/endpoints/branchesApi';
import {
  ComplianceDocumentType,
  complianceDocumentDefinitionsApi,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import {
  SupplierComplianceDocumentFile,
  supplierComplianceDocumentsApi,
} from '@texas/api/endpoints/supplierComplianceDocumentsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { EntityDirectoryFileThumbnail } from '@texas/components/shared/entityDirectory/EntityDirectoryFileThumbnail';
import { FileUploads } from '@texas/components/shared/dropzone/FileUploads';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ReactSelectOption } from '@texas/types';
import { supplierComplianceDocumentEvents } from './events';

interface Props extends SharedDisclosureProps {
  supplierId: number;
  documentsFolderId: number;
  branches: Branch[];
}

interface FormValues {
  complianceDocumentDefinitionId: string;
  fileLinkId: number | null;
  label: string;
  description: string;
  startDate: string;
  endDate: string;
  branchIds: number[];
}

const defaultValues: FormValues = {
  complianceDocumentDefinitionId: '',
  fileLinkId: null,
  label: '',
  description: '',
  startDate: '',
  endDate: '',
  branchIds: [],
};

export function CreateSupplierComplianceDocument({
  supplierId,
  documentsFolderId,
  branches,
  isOpen,
  onClose,
}: Props) {
  return (
    <Modal
      variant="entityDirectory"
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      {isOpen && (
        <Inner
          supplierId={supplierId}
          documentsFolderId={documentsFolderId}
          branches={branches}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

function Inner({
  supplierId,
  documentsFolderId,
  branches,
  onClose,
}: Omit<Props, 'isOpen'>) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [documentFile, setDocumentFile] =
    useState<SupplierComplianceDocumentFile | null>(null);
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues });
  const {
    data: definitions,
    refetch: refetchDefinitions,
    loading: definitionsLoading,
    error: definitionsError,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const {
    request: create,
    loading,
    error,
  } = useApiRequest(supplierComplianceDocumentsApi.create);
  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
    resetFileUploads,
  } = useFileUploads((file) => {
    setDocumentFile(file);
    setValue('fileLinkId', file.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
  });
  const selectedDefinitionId = watch('complianceDocumentDefinitionId');
  const selectedDefinition = definitions?.find(
    ({ id }) => id === Number(selectedDefinitionId),
  );
  const branchOptions = useMemo<ReactSelectOption[]>(
    () =>
      branches.map((branch) => ({
        label: `${branch.name} (${branch.identifier})`,
        value: branch.id,
      })),
    [branches],
  );

  useEffect(() => {
    refetchDefinitions({
      searchTerm: '',
      includeArchived: false,
      sortBy: 'name',
      sortDesc: false,
    });
  }, [refetchDefinitions]);

  const close = () => {
    reset(defaultValues);
    setDocumentFile(null);
    resetFileUploads();
    onClose();
  };

  const uploadDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setDocumentFile(null);
      setValue('fileLinkId', null, { shouldDirty: true });
      uploadFiles([file], documentsFolderId);
    }
    event.target.value = '';
  };

  const submit = async (values: FormValues) => {
    if (values.fileLinkId === null) return;
    const response = await create({
      supplierId,
      complianceDocumentDefinitionId: Number(
        values.complianceDocumentDefinitionId,
      ),
      fileLinkId: values.fileLinkId,
      label: values.label.trim() || null,
      description: values.description.trim() || null,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      branchIds: values.branchIds,
    });
    if (response.hasError) return;

    toast({
      title: t('general.created'),
      status: 'success',
      isClosable: true,
    });
    supplierComplianceDocumentEvents.changed.dispatch(supplierId);
    close();
  };

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalHeader>
        {t('suppliers.workspace.createDocument')}
        <Text
          mt={1}
          textTransform="none"
          variant="entity-directory-description"
        >
          {t('suppliers.workspace.createDocumentDescription')}
        </Text>
      </ModalHeader>
      <form onSubmit={handleSubmit(submit)}>
        <ModalBody display="flex" flexDirection="column" gap={5}>
          <ErrorDetails error={definitionsError} />
          <ErrorDetails error={error} />
          <FormControl
            isRequired={true}
            isInvalid={!!errors.complianceDocumentDefinitionId}
          >
            <TexasFormLabel>
              {t('suppliers.workspace.configuration')}
            </TexasFormLabel>
            <Select
              size="sm"
              variant="entityDirectory"
              isDisabled={definitionsLoading}
              placeholder={t('general.select')}
              {...register('complianceDocumentDefinitionId', {
                required: true,
              })}
            >
              {(definitions ?? [])
                .filter(({ type }) =>
                  compareEnum(
                    ComplianceDocumentType,
                    type,
                    ComplianceDocumentType.Scoped,
                  ),
                )
                .map((definition) => (
                  <option key={definition.id} value={definition.id}>
                    {definition.name}
                  </option>
                ))}
            </Select>
            <ErrorLabel text={errors.complianceDocumentDefinitionId?.message} />
            {selectedDefinition && (
              <Flex
                align="center"
                bg="entityDirectory.raisedSurface"
                border="1px solid"
                borderColor="entityDirectory.border"
                gap={3}
                minH="92px"
                mt={3}
                p={3}
              >
                <EntityDirectoryFileThumbnail
                  alt={selectedDefinition.name}
                  file={selectedDefinition.logo}
                />
                <Text
                  variant="entity-directory-description"
                  whiteSpace="pre-wrap"
                >
                  {selectedDefinition.description}
                </Text>
              </Flex>
            )}
          </FormControl>
          <FormControl>
            <TexasFormLabel>
              {t('suppliers.workspace.branchesOptional')}
            </TexasFormLabel>
            <Controller
              control={control}
              name="branchIds"
              render={({ field }) => (
                <TexasSelect<ReactSelectOption, true>
                  closeMenuOnSelect={false}
                  isClearable={true}
                  isMulti={true}
                  options={branchOptions}
                  placeholder={t('general.select')}
                  usePortal={true}
                  value={branchOptions.filter((option) =>
                    field.value.includes(option.value),
                  )}
                  onBlur={field.onBlur}
                  onChange={(options) =>
                    field.onChange(options.map((option) => option.value))
                  }
                />
              )}
            />
            <Text variant="entity-directory-description">
              {t('suppliers.workspace.branchesHelp')}
            </Text>
          </FormControl>
          <FormControl isRequired={true} isInvalid={!!errors.fileLinkId}>
            <TexasFormLabel>{t('suppliers.workspace.document')}</TexasFormLabel>
            <input
              type="hidden"
              {...register('fileLinkId', { required: true })}
            />
            <input
              ref={fileInput}
              type="file"
              hidden={true}
              onChange={uploadDocument}
            />
            <Flex
              align="center"
              bg="entityDirectory.raisedSurface"
              border="1px solid"
              borderColor="entityDirectory.border"
              gap={3}
              justify="space-between"
              minH="76px"
              p={3}
            >
              <Flex direction="column" flex={1} gap={1} minW={0}>
                <Text
                  fontSize="13px"
                  noOfLines={1}
                  variant="configuration-strong"
                >
                  {documentFile?.originalName ??
                    t('suppliers.workspace.uploadDocument')}
                </Text>
                <Text fontSize="12px" variant="entity-directory-description">
                  {t('suppliers.workspace.documentUploadHelp')}
                </Text>
              </Flex>
              <Button
                type="button"
                variant="entityDirectorySecondary"
                isDisabled={isUploading}
                onClick={() => fileInput.current?.click()}
              >
                {documentFile
                  ? t('general.replace')
                  : t('complianceDocumentDefinitions.chooseFile')}
              </Button>
            </Flex>
            <FileUploads
              fileUploads={fileUploads}
              removeFileUpload={removeFileUpload}
            />
            <ErrorLabel text={errors.fileLinkId?.message} />
          </FormControl>
          <FormControl isInvalid={!!errors.label}>
            <TexasFormLabel>
              {t('suppliers.workspace.labelOptional')}
            </TexasFormLabel>
            <Input
              variant="entityDirectory"
              {...register('label', {
                maxLength: {
                  value: 100,
                  message: t('errors.maxLength', { count: 100 }),
                },
              })}
            />
            <HookFormCounter control={control} name="label" maxLength={100} />
            <Text fontSize="12px" mt={1} variant="entity-directory-description">
              {t('suppliers.workspace.labelHelp')}
            </Text>
            <ErrorLabel text={errors.label?.message} />
          </FormControl>
          <FormControl isInvalid={!!errors.description}>
            <TexasFormLabel>
              {t('suppliers.workspace.descriptionOptional')}
            </TexasFormLabel>
            <Textarea
              minH="92px"
              resize="vertical"
              variant="entityDirectory"
              {...register('description', {
                maxLength: {
                  value: 1000,
                  message: t('errors.maxLength', { count: 1000 }),
                },
              })}
            />
            <HookFormCounter
              control={control}
              name="description"
              maxLength={1000}
            />
            <ErrorLabel text={errors.description?.message} />
          </FormControl>
          <Grid
            gap={3}
            gridTemplateColumns={{
              base: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
            }}
          >
            <FormControl>
              <TexasFormLabel>
                {t('suppliers.workspace.startDateOptional')}
              </TexasFormLabel>
              <Input
                type="date"
                variant="entityDirectory"
                {...register('startDate')}
              />
            </FormControl>
            <FormControl>
              <TexasFormLabel>
                {t('suppliers.workspace.endDateOptional')}
              </TexasFormLabel>
              <Input
                type="date"
                variant="entityDirectory"
                {...register('endDate')}
              />
            </FormControl>
          </Grid>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="entityDirectorySecondary" onClick={close}>
            {t('general.cancel')}
          </Button>
          <Button
            type="submit"
            variant="entityDirectoryPrimary"
            isLoading={loading}
            isDisabled={
              definitionsLoading || isUploading || uploadFailed || !documentFile
            }
          >
            {t('suppliers.workspace.createDocument')}
          </Button>
        </ModalFooter>
      </form>
    </ModalContent>
  );
}
